'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { createClient } from '@/lib/supabase/client'
import { buscarMeuPerfil } from '@/lib/perfil/meu-perfil'
import TransparentCheckout from '@/components/ui/transparent-checkout'
import { SiteFooter } from '@/components/ui/site-footer'

export default function ComboSeoGeoPage() {
  const [faqOpen, setFaqOpen] = useState<number | null>(null)
  const [showCheckout, setShowCheckout] = useState(false)
  const [checkoutError, setCheckoutError] = useState('')
  const [buyerName, setBuyerName] = useState<string | undefined>()
  const [buyerEmail, setBuyerEmail] = useState<string | undefined>()
  const [buyerPhone, setBuyerPhone] = useState<string | undefined>()
  const [buyerCpf, setBuyerCpf] = useState<string | undefined>()
  const router = useRouter()

  useEffect(() => {
    const supabase = createClient()
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        setBuyerEmail(user.email || undefined)
        setBuyerName(user.user_metadata?.full_name || undefined)
        buscarMeuPerfil()
          .then(({ data: profile }) => {
            if (profile) {
              if (profile.full_name) setBuyerName(profile.full_name)
              if (profile.phone) setBuyerPhone(profile.phone)
              if (profile.document) setBuyerCpf(profile.document)
            }
          })
      }
    })
  }, [])

  function handleBuy() {
    setShowCheckout(true)
    setCheckoutError('')
  }

  function scrollToCheckout() {
    handleBuy()
    window.scrollTo({ top: document.getElementById('pricing')?.offsetTop || 0, behavior: 'smooth' })
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Barra topo */}
      <div className="bg-gradient-to-r from-emerald-600 to-cyan-600 text-white text-center py-2 px-3 text-xs sm:text-sm font-medium">
        74 aulas. 2 cursos completos. Economize R$ 97 no combo.
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-emerald-900/40 via-cyan-900/20 to-transparent" />
        <div className="relative max-w-5xl mx-auto px-4 pt-10 pb-12 sm:pt-16 sm:pb-20">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-sky-500/15 border border-sky-500/25 text-sky-300 rounded-full text-[11px] sm:text-xs font-semibold mb-5 sm:mb-6 uppercase tracking-wide">
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
              Combo exclusivo - Economize R$ 97
            </div>

            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 leading-tight">
              Combo SEO/GEO{' '}
              <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
                Trilha Completa
              </span>
            </h1>

            <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-3 sm:mb-4 leading-relaxed max-w-2xl mx-auto">
              Pré-SEO/GEO + SEO/GEO Pro juntos. Da compreensão da mudança na busca
              até a implementação técnica e monetização como profissional.
            </p>

            <div className="flex flex-wrap justify-center gap-2 mb-6 sm:mb-8">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/15 border border-emerald-500/25 text-emerald-300 rounded-full text-xs font-semibold">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                Pré-SEO/GEO incluso
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-cyan-500/15 border border-cyan-500/25 text-cyan-300 rounded-full text-xs font-semibold">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                SEO/GEO Pro incluso
              </span>
            </div>

            {/* Preco hero */}
            <div className="mb-6 sm:mb-8">
              <span className="text-gray-500 line-through text-lg sm:text-xl mr-2">R$ 594</span>
              <span className="text-3xl sm:text-4xl md:text-5xl font-black text-white">R$ 497</span>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center mb-6 sm:mb-8">
              <button onClick={handleBuy}
                className="w-full sm:w-auto bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 text-white font-bold py-3.5 sm:py-4 px-8 sm:px-10 rounded-xl text-base sm:text-lg transition shadow-lg shadow-emerald-600/30 cursor-pointer">
                GARANTIR COMBO POR R$ 497
              </button>
              <p className="text-xs sm:text-sm text-gray-400">Pagamento unico. Acesso vitalicio.</p>
            </div>

            {/* Badges */}
            <div className="flex flex-wrap justify-center gap-2 sm:gap-3 text-xs sm:text-sm">
              <span className="bg-white/5 border border-white/10 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full text-gray-300">74 aulas</span>
              <span className="bg-white/5 border border-white/10 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full text-gray-300">17 modulos</span>
              <span className="bg-white/5 border border-white/10 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full text-gray-300">~17h de conteudo</span>
              <span className="bg-white/5 border border-white/10 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full text-gray-300">29 referencias cientificas</span>
              <span className="bg-white/5 border border-white/10 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full text-gray-300">Audio profissional</span>
            </div>
          </div>
        </div>
      </section>

      {/* Dois cursos, uma trilha */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-gray-900">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-3">
            Dois cursos, uma trilha completa
          </h2>
          <p className="text-sm sm:text-base text-gray-400 text-center mb-10 sm:mb-14 max-w-2xl mx-auto">
            O Pré ensina o contexto e a estrategia. O Pro entrega a implementacao tecnica.
            Juntos, cobrem toda a jornada da visibilidade digital na era da IA.
          </p>

          <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
            {/* Pre-SEO/GEO */}
            <div className="bg-gradient-to-b from-emerald-950/50 to-gray-900/50 border border-emerald-500/20 rounded-2xl p-6 sm:p-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-500/15 border border-emerald-500/25 text-emerald-400 rounded-full text-[11px] font-semibold mb-4 uppercase">
                Curso 1 - Fundamentos
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">Pre-SEO/GEO</h3>
              <p className="text-sm text-emerald-200/70 mb-1">Visibilidade na Era da IA</p>
              <p className="text-xs text-gray-500 mb-5">Para donos de negocio - 32 aulas, 7 modulos</p>

              <div className="space-y-2.5 mb-6">
                {[
                  { mod: 'M0', title: 'Degustacao: O Mundo Mudou', aulas: 4, free: true },
                  { mod: 'M1', title: 'Como a IA Responde Perguntas', aulas: 5, free: false },
                  { mod: 'M2', title: 'Os 3 Pilares da Visibilidade', aulas: 5, free: false },
                  { mod: 'M3', title: 'Google Meu Negocio', aulas: 5, free: false },
                  { mod: 'M4', title: 'Conteudo que a IA Recomenda', aulas: 5, free: false },
                  { mod: 'M5', title: 'Seu Plano de Acao Completo', aulas: 4, free: false },
                  { mod: 'M6', title: 'Bonus: Cases e Conexoes', aulas: 4, free: false },
                ].map((m) => (
                  <div key={m.mod} className="flex items-center gap-3 text-sm">
                    <span className="text-emerald-400 font-mono text-xs bg-emerald-500/10 px-2 py-0.5 rounded shrink-0">{m.mod}</span>
                    <span className="text-gray-300">{m.title}</span>
                    <span className="text-gray-600 text-xs ml-auto shrink-0 flex items-center gap-1.5">
                      {m.free && <span className="text-emerald-400 text-[10px] font-semibold">GRATIS</span>}
                      {m.aulas} aulas
                    </span>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-400 border-t border-gray-700/50 pt-4">
                <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                32 aulas - ~7h de conteudo
              </div>
            </div>

            {/* SEO/GEO Pro */}
            <div className="bg-gradient-to-b from-cyan-950/50 to-gray-900/50 border border-cyan-500/20 rounded-2xl p-6 sm:p-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-cyan-500/15 border border-cyan-500/25 text-cyan-400 rounded-full text-[11px] font-semibold mb-4 uppercase">
                Curso 2 - Profissional
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">SEO/GEO Pro</h3>
              <p className="text-sm text-cyan-200/70 mb-1">Otimizacao para IA Generativa</p>
              <p className="text-xs text-gray-500 mb-5">Para profissionais - 42 aulas, 10 modulos</p>

              <div className="space-y-2.5 mb-6">
                {[
                  { mod: 'M0', title: 'Degustacao: O Profissional do Futuro', aulas: 4, free: true },
                  { mod: 'M1', title: 'Fundamentos Tecnicos de SEO', aulas: 5, free: false },
                  { mod: 'M2', title: 'Schema.org e Dados Estruturados', aulas: 5, free: false },
                  { mod: 'M3', title: 'Google Merchant Center', aulas: 4, free: false },
                  { mod: 'M4', title: 'GEO: Otimizacao para IA', aulas: 5, free: false },
                  { mod: 'M5', title: 'Link Building e Autoridade', aulas: 4, free: false },
                  { mod: 'M6', title: 'SEO Tecnico Avancado', aulas: 4, free: false },
                  { mod: 'M7', title: 'Ferramentas e Automacao', aulas: 4, free: false },
                  { mod: 'M8', title: 'Vendendo Servicos de SEO/GEO', aulas: 4, free: false },
                  { mod: 'M9', title: 'Bonus: Cases Reais e Futuro', aulas: 3, free: false },
                ].map((m) => (
                  <div key={m.mod} className="flex items-center gap-3 text-sm">
                    <span className="text-cyan-400 font-mono text-xs bg-cyan-500/10 px-2 py-0.5 rounded shrink-0">{m.mod}</span>
                    <span className="text-gray-300">{m.title}</span>
                    <span className="text-gray-600 text-xs ml-auto shrink-0 flex items-center gap-1.5">
                      {m.free && <span className="text-cyan-400 text-[10px] font-semibold">GRATIS</span>}
                      {m.aulas} aulas
                    </span>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-400 border-t border-gray-700/50 pt-4">
                <svg className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                42 aulas - ~10h de conteudo
              </div>
            </div>
          </div>

          {/* Seta conectando */}
          <div className="flex justify-center my-8">
            <div className="flex items-center gap-3 px-5 py-2.5 bg-sky-500/10 border border-sky-500/20 rounded-full">
              <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" /></svg>
              <span className="text-sm text-sky-300 font-medium">Entender</span>
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              <span className="text-sm text-sky-300 font-medium">Implementar</span>
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              <span className="text-sm text-sky-300 font-medium">Monetizar</span>
              <svg className="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" /></svg>
            </div>
          </div>
        </div>
      </section>

      {/* O que esta incluso - detalhado */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-gray-950">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-8 sm:mb-12">
            O que voce recebe no combo
          </h2>

          <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
            {[
              { icon: '📚', text: '74 aulas em audio profissional (Google TTS Chirp3-HD)' },
              { icon: '🎯', text: '32 aulas de fundamentos (Pre-SEO/GEO) para donos de negocio' },
              { icon: '🚀', text: '42 aulas tecnicas (SEO/GEO Pro) para profissionais' },
              { icon: '🔬', text: '29 referencias cientificas incluindo paper GEO do KDD 2024' },
              { icon: '🗺️', text: 'Google Meu Negocio: setup completo e otimizacao' },
              { icon: '🏷️', text: 'Schema.org e dados estruturados na pratica' },
              { icon: '🛒', text: 'Google Merchant Center: do zero a visibilidade' },
              { icon: '📊', text: 'Ferramentas profissionais: GSC, Screaming Frog, dashboards' },
              { icon: '💰', text: 'Modulo de precificacao e venda de servicos SEO/GEO' },
              { icon: '📋', text: '8 aulas gratuitas de degustacao (4 em cada curso)' },
              { icon: '💼', text: 'Cases reais detalhados com metricas antes/depois' },
              { icon: '♾️', text: 'Acesso vitalicio a ambos os cursos' },
            ].map((item, i) => (
              <div key={i} className="flex gap-3 items-start bg-gray-800/30 border border-gray-700/30 rounded-xl p-4">
                <span className="text-xl shrink-0">{item.icon}</span>
                <p className="text-sm text-gray-300">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tabela comparativa */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-3">
            Compare e escolha
          </h2>
          <p className="text-sm sm:text-base text-gray-400 text-center mb-8 sm:mb-12 max-w-2xl mx-auto">
            Cada curso funciona sozinho, mas juntos cobrem toda a jornada.
          </p>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-3 px-4 text-gray-400 font-medium"></th>
                  <th className="text-center py-3 px-4 text-emerald-400 font-semibold">Pre-SEO/GEO</th>
                  <th className="text-center py-3 px-4 text-cyan-400 font-semibold">SEO/GEO Pro</th>
                  <th className="text-center py-3 px-4 font-semibold">
                    <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">Combo</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {[
                  { feature: 'Aulas', pre: '32', pro: '42', combo: '74' },
                  { feature: 'Modulos', pre: '7', pro: '10', combo: '17' },
                  { feature: 'Duracao', pre: '~7h', pro: '~10h', combo: '~17h' },
                  { feature: 'Publico-alvo', pre: 'Donos de negocio', pro: 'Profissionais', combo: 'Ambos' },
                  { feature: 'Aulas gratis', pre: '4', pro: '4', combo: '8' },
                  { feature: 'Google Meu Negocio', pre: 'check', pro: '-', combo: 'check' },
                  { feature: 'Schema.org', pre: '-', pro: 'check', combo: 'check' },
                  { feature: 'Merchant Center', pre: '-', pro: 'check', combo: 'check' },
                  { feature: 'Tecnicas GEO (KDD 2024)', pre: 'Visao geral', pro: 'Implementacao', combo: 'Completo' },
                  { feature: 'Vender servicos', pre: '-', pro: 'check', combo: 'check' },
                  { feature: 'Preco', pre: 'R$ 297', pro: 'R$ 297', combo: 'R$ 497' },
                ].map((row, i) => (
                  <tr key={i} className={i === 10 ? 'bg-sky-500/5' : ''}>
                    <td className="py-3 px-4 text-gray-300 font-medium">{row.feature}</td>
                    <td className="py-3 px-4 text-center text-gray-400">
                      {row.pre === 'check' ? (
                        <svg className="w-5 h-5 text-emerald-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                      ) : row.pre === '-' ? (
                        <span className="text-gray-700">-</span>
                      ) : row.pre}
                    </td>
                    <td className="py-3 px-4 text-center text-gray-400">
                      {row.pro === 'check' ? (
                        <svg className="w-5 h-5 text-cyan-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                      ) : row.pro === '-' ? (
                        <span className="text-gray-700">-</span>
                      ) : row.pro}
                    </td>
                    <td className="py-3 px-4 text-center font-semibold">
                      {row.combo === 'check' ? (
                        <svg className="w-5 h-5 text-sky-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                      ) : i === 10 ? (
                        <span className="text-sky-300">{row.combo}</span>
                      ) : (
                        <span className="text-white">{row.combo}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="text-center mt-8">
            <button onClick={handleBuy}
              className="bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 text-white font-bold py-3.5 px-8 rounded-xl text-base transition shadow-lg shadow-emerald-600/30 cursor-pointer">
              QUERO O COMBO COM DESCONTO
            </button>
          </div>
        </div>
      </section>

      {/* Jornada visual */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-gray-950">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-8 sm:mb-12">
            Sua jornada em 5 fases
          </h2>

          <div className="space-y-4 sm:space-y-6">
            {[
              {
                phase: '01',
                title: 'Entenda a mudanca',
                desc: 'O Google deixou de mostrar 10 links azuis. Agora a IA responde direto. Voce entende por que isso muda tudo para quem tem um negocio.',
                color: 'emerald',
              },
              {
                phase: '02',
                title: 'Organize sua presenca',
                desc: 'Google Meu Negocio otimizado, conteudo que a IA recomenda, os 3 pilares da visibilidade moderna aplicados ao seu caso.',
                color: 'emerald',
              },
              {
                phase: '03',
                title: 'Implemente o tecnico',
                desc: 'Schema.org, dados estruturados, Merchant Center, SEO tecnico avancado. Seu site fala a lingua das maquinas.',
                color: 'cyan',
              },
              {
                phase: '04',
                title: 'Domine GEO',
                desc: 'Otimizacao para IA generativa baseada no paper do KDD 2024. Tecnicas cientificas para ser citado por ChatGPT, Gemini e Perplexity.',
                color: 'cyan',
              },
              {
                phase: '05',
                title: 'Monetize o conhecimento',
                desc: 'Precifique e venda servicos de SEO/GEO. Propostas, workflow de clientes, como escalar de freelancer a agencia.',
                color: 'sky',
              },
            ].map((item) => (
              <div key={item.phase} className="flex gap-4 sm:gap-6 items-start">
                <div className={`text-2xl sm:text-3xl font-black shrink-0 w-10 sm:w-14 ${
                  item.color === 'emerald' ? 'text-emerald-500/30' :
                  item.color === 'cyan' ? 'text-cyan-500/30' : 'text-sky-500/30'
                }`}>
                  {item.phase}
                </div>
                <div className="pb-4 sm:pb-6 border-b border-gray-800">
                  <h3 className="text-base sm:text-lg font-bold text-white mb-1">{item.title}</h3>
                  <p className="text-xs sm:text-sm text-gray-400 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sobre o instrutor */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-8 sm:mb-12">
            Sobre o instrutor
          </h2>

          <div className="bg-gray-800/40 border border-gray-700/40 rounded-2xl p-6 sm:p-8 md:p-10">
            <div className="flex flex-col sm:flex-row gap-6 items-start">
              <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-2xl flex items-center justify-center shrink-0 text-3xl sm:text-4xl font-bold text-white">
                DA
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-1">Denis Alves</h3>
                <p className="text-sm text-sky-400 mb-4">Especialista em SEO/GEO e Visibilidade Digital</p>
                <div className="space-y-3 text-sm text-gray-300 leading-relaxed">
                  <p>
                    Denis trabalha com SEO ha mais de uma decada e foi um dos primeiros profissionais
                    no Brasil a se especializar em GEO (Generative Engine Optimization) - a arte
                    de fazer negocios serem citados pelas IAs generativas.
                  </p>
                  <p>
                    Combina a experiencia pratica de quem ja otimizou centenas de sites com a
                    base cientifica das pesquisas mais recentes, incluindo o paper referencia
                    sobre GEO apresentado no KDD 2024.
                  </p>
                  <p>
                    Nos dois cursos, Denis traduz conceitos tecnicos complexos em linguagem
                    acessivel para donos de negocio (Pre-SEO/GEO) e entrega implementacao
                    profunda para quem quer atuar na area (SEO/GEO Pro).
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Base cientifica */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-gray-950">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-3">
            Base cientifica solida
          </h2>
          <p className="text-sm sm:text-base text-gray-400 text-center mb-8 sm:mb-12 max-w-2xl mx-auto">
            O conteudo nao e achismo. Sao 29 referencias cientificas e tecnicas que sustentam cada modulo.
          </p>

          <div className="grid sm:grid-cols-3 gap-4 sm:gap-5">
            {[
              {
                title: 'Paper GEO - KDD 2024',
                desc: 'Aggarwal et al. - 9 estrategias de otimizacao para motores generativos testadas cientificamente.',
                tag: 'Pesquisa',
              },
              {
                title: 'Google Search Central',
                desc: 'Documentacao oficial sobre dados estruturados, Schema.org, Merchant Center e Search Console.',
                tag: 'Documentacao',
              },
              {
                title: 'Core Web Vitals',
                desc: 'Metricas de performance (LCP, INP, CLS) e seu impacto em ranking e experiencia do usuario.',
                tag: 'Performance',
              },
              {
                title: 'E-E-A-T Framework',
                desc: 'Experience, Expertise, Authoritativeness, Trustworthiness - os pilares de qualidade do Google.',
                tag: 'Qualidade',
              },
              {
                title: 'RAG Architecture',
                desc: 'Retrieval-Augmented Generation - como LLMs decidem quais fontes citar nas respostas.',
                tag: 'IA',
              },
              {
                title: 'Digital PR Research',
                desc: 'Estudos sobre construcao de autoridade digital, link building etico e mencoes em fontes autoritativas.',
                tag: 'Autoridade',
              },
            ].map((ref, i) => (
              <div key={i} className="bg-gray-800/40 border border-gray-700/40 rounded-xl p-5">
                <span className="inline-block px-2 py-0.5 bg-sky-500/10 text-sky-400 text-[10px] font-semibold rounded-full mb-3 uppercase">{ref.tag}</span>
                <h3 className="text-sm font-bold text-white mb-1.5">{ref.title}</h3>
                <p className="text-xs text-gray-400 leading-relaxed">{ref.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Para quem e / nao e */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-5 flex items-center gap-2">
                <span className="text-green-500">&#10003;</span> Para quem e
              </h3>
              <div className="space-y-3">
                {[
                  'Donos de negocio que querem ser encontrados no Google e nas IAs',
                  'Profissionais de marketing que precisam dominar SEO/GEO',
                  'Freelancers que querem vender servicos de visibilidade digital',
                  'E-commerces que precisam aparecer no Google Shopping e nas respostas de IA',
                  'Quem ja tem um site mas nao aparece nas buscas',
                  'Agencias que querem adicionar SEO/GEO ao portfolio',
                ].map((item, i) => (
                  <div key={i} className="flex gap-3 items-start">
                    <svg className="w-5 h-5 text-green-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                    <p className="text-sm text-gray-300">{item}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-5 flex items-center gap-2">
                <span className="text-red-500">&#10005;</span> Para quem nao e
              </h3>
              <div className="space-y-3">
                {[
                  'Quem busca resultado imediato sem trabalho consistente',
                  'Especialistas em SEO com 5+ anos que ja dominam GEO',
                  'Quem espera um curso de trafego pago ou redes sociais',
                  'Pessoas que nao tem um negocio ou site para aplicar o conhecimento',
                ].map((item, i) => (
                  <div key={i} className="flex gap-3 items-start">
                    <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                    <p className="text-sm text-gray-300">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-12 sm:py-16 md:py-20 px-4 bg-gray-950">
        <div className="max-w-xl mx-auto">
          <div className="bg-gradient-to-b from-sky-950/60 to-gray-900/60 border border-sky-500/30 rounded-2xl p-6 sm:p-10 text-center relative overflow-hidden">
            {/* Badge economia */}
            <div className="absolute top-4 right-4">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 rounded-full text-[11px] font-bold">
                Economize R$ 97
              </span>
            </div>

            <div className="text-xs sm:text-sm text-sky-300 font-semibold uppercase tracking-wide mb-4">
              Combo SEO/GEO - Pre + Pro - Trilha Completa
            </div>

            <div className="mb-2">
              <span className="text-gray-500 line-through text-lg sm:text-xl">De R$ 594</span>
            </div>

            <div className="mb-1">
              <span className="text-4xl sm:text-5xl md:text-6xl font-black text-white">R$ 497</span>
            </div>
            <p className="text-sm text-gray-400 mb-2">
              Pagamento unico. Sem mensalidade. Acesso pra sempre.
            </p>
            <p className="text-xs text-emerald-400 mb-6 sm:mb-8 font-medium">
              Voce economiza R$ 97 comprando o combo
            </p>

            <button onClick={handleBuy}
              className="w-full bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 text-white font-bold py-4 px-8 rounded-xl text-lg transition shadow-lg shadow-emerald-600/30 mb-4 cursor-pointer">
              QUERO O COMBO POR R$ 497
            </button>

            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-6 text-xs sm:text-sm text-gray-400">
              <span className="flex items-center justify-center gap-1.5">
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                Pagamento seguro
              </span>
              <span className="flex items-center justify-center gap-1.5">
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
                PIX, cartao ou boleto
              </span>
              <span className="flex items-center justify-center gap-1.5">
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                7 dias de garantia
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-gray-900">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-8 sm:mb-12">
            Perguntas frequentes
          </h2>

          <div className="space-y-3">
            {[
              {
                q: 'Qual a diferenca entre Pre-SEO/GEO e SEO/GEO Pro?',
                a: 'O Pre-SEO/GEO e para donos de negocio que precisam entender a mudanca e aplicar o basico: Google Meu Negocio, conteudo otimizado, plano de acao. O SEO/GEO Pro e para profissionais que querem dominar Schema.org, Merchant Center, tecnicas de GEO e vender servicos. O combo cobre toda a jornada.',
              },
              {
                q: 'Posso comprar so um dos cursos?',
                a: 'Sim, cada curso e vendido separadamente por R$ 297. No combo voce leva os dois por R$ 497, economizando R$ 97. Se voce quer a trilha completa, o combo e a escolha mais inteligente.',
              },
              {
                q: 'Preciso saber de tecnologia?',
                a: 'Para o Pre-SEO/GEO, nao. Ele foi pensado para donos de negocio sem background tecnico. O SEO/GEO Pro exige um pouco mais de familiaridade com tecnologia (HTML basico, navegacao em ferramentas), mas tudo e ensinado do zero.',
              },
              {
                q: 'O que sao as aulas gratis de degustacao?',
                a: 'Cada curso tem um modulo 0 gratuito com 4 aulas. Sao 8 aulas gratis no total para voce experimentar o conteudo antes de comprar. No Pre voce entende a mudanca na busca. No Pro voce ve a auditoria profissional em acao.',
              },
              {
                q: 'O que e GEO?',
                a: 'GEO significa Generative Engine Optimization. E a pratica de otimizar conteudo para ser citado por IAs generativas como ChatGPT, Gemini e Perplexity. Diferente do SEO tradicional que foca em links azuis no Google, GEO foca em ser a fonte que a IA recomenda.',
              },
              {
                q: 'Quanto tempo tenho para fazer os cursos?',
                a: 'Acesso vitalicio. Faca no seu ritmo, revise quantas vezes quiser. As aulas sao em audio, entao voce pode ouvir no carro, na academia ou enquanto trabalha.',
              },
              {
                q: 'O conteudo tem base cientifica?',
                a: 'Sim. Sao 29 referencias cientificas e tecnicas, incluindo o paper de referencia sobre GEO apresentado na conferencia KDD 2024 e toda a documentacao oficial do Google Search Central.',
              },
              {
                q: 'E se eu nao gostar?',
                a: 'Garantia incondicional de 7 dias. Pediu reembolso, devolvemos 100%. Sem burocracia.',
              },
            ].map((item, i) => (
              <div key={i} className="bg-gray-950/50 border border-gray-800 rounded-xl overflow-hidden">
                <button
                  onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                  className="w-full flex justify-between items-center p-4 sm:p-5 text-left cursor-pointer"
                >
                  <span className="text-sm sm:text-base font-medium text-white pr-4">{item.q}</span>
                  <svg className={`w-5 h-5 text-gray-400 shrink-0 transition-transform ${faqOpen === i ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {faqOpen === i && (
                  <div className="px-4 sm:px-5 pb-4 sm:pb-5">
                    <p className="text-xs sm:text-sm text-gray-400 leading-relaxed">{item.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-gradient-to-b from-gray-900 to-gray-950">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4">
            A busca mudou. O seu negocio ja se adaptou?
          </h2>
          <p className="text-sm sm:text-base text-gray-400 mb-6 sm:mb-8">
            74 aulas. Do entendimento a implementacao. Acesso vitalicio. R$ 497.
          </p>

          <button onClick={handleBuy}
            className="bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 text-white font-bold py-3.5 sm:py-4 px-8 sm:px-10 rounded-xl text-base sm:text-lg transition shadow-lg shadow-emerald-600/30 mb-4 cursor-pointer">
            GARANTIR MEU COMBO AGORA
          </button>
          <p className="text-xs text-gray-500">Pagamento seguro via Assas. PIX, cartao ou boleto.</p>
        </div>
      </section>

      {/* Checkout Modal */}
      {showCheckout && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowCheckout(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white rounded-t-2xl border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Finalizar compra</h3>
                <p className="text-sm text-gray-500">Combo SEO/GEO - Pre + Pro - Trilha Completa</p>
              </div>
              <button onClick={() => setShowCheckout(false)} className="text-gray-400 hover:text-gray-600 p-1 cursor-pointer">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="p-6">
              {checkoutError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {checkoutError}
                </div>
              )}
              <TransparentCheckout
                channelSlug="combo-seo-geo"
                buyerName={buyerName}
                buyerEmail={buyerEmail}
                buyerPhone={buyerPhone}
                buyerCpf={buyerCpf}
                onSuccess={(paymentId) => {
                  router.push('/obrigado?payment=' + paymentId)
                }}
                onError={(error) => {
                  setCheckoutError(error)
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <SiteFooter />
    </div>
  )
}
